/** Resolve venue product id from wizard or API ingredient shapes. */
export const resolveIngredientVenueProductId = ingredient => {
  const id =
    ingredient?.venueProductId ||
    ingredient?.productId ||
    ingredient?.product?.venueProductId ||
    ingredient?._id

  return id ? String(id) : ''
}

export const resolveIngredientCatalogueProductId = ingredient => {
  const product = ingredient?.product
  const candidates = [
    typeof product === 'string' ? product : null,
    product?.product?._id,
    product?._id,
    product?.id,
    ingredient?.catalogueProductId,
  ]

  for (const candidate of candidates) {
    if (candidate) {
      return String(candidate)
    }
  }

  return ''
}

export const buildShoppingListProductIdSet = (products = []) => {
  const ids = new Set()

  ;(products || []).forEach(product => {
    if (product?._id) ids.add(String(product._id))
    if (product?.id) ids.add(String(product.id))
    if (product?.product?._id) ids.add(String(product.product._id))
    if (product?.product?.id) ids.add(String(product.product.id))
  })

  return ids
}

const getIngredientShoppingListLookupKeys = ingredient => {
  const keys = new Set()
  const venueProductId = resolveIngredientVenueProductId(ingredient)
  const catalogueProductId = resolveIngredientCatalogueProductId(ingredient)

  if (venueProductId) {
    keys.add(venueProductId)
  }
  if (catalogueProductId) {
    keys.add(catalogueProductId)
  }

  return keys
}

export const isIngredientOnShoppingList = (
  ingredient,
  shoppingListProductIds,
) => {
  if (!shoppingListProductIds?.size) {
    return false
  }

  for (const key of getIngredientShoppingListLookupKeys(ingredient)) {
    if (shoppingListProductIds.has(key)) {
      return true
    }
  }

  return false
}

export const resolveIngredientDisplayTitle = ingredient => {
  const candidates = [
    ingredient?.title,
    ingredient?.product?.title,
    ingredient?.product?.name,
    ingredient?.name,
  ]

  for (const candidate of candidates) {
    const trimmed = String(candidate || '').trim()
    if (trimmed) {
      return trimmed
    }
  }

  return ''
}

export const resolveIngredientDisplaySku = ingredient => {
  const candidates = [ingredient?.sku, ingredient?.product?.sku]

  for (const candidate of candidates) {
    const trimmed = String(candidate || '').trim()
    if (trimmed) {
      return trimmed
    }
  }

  return ''
}

const getIncompleteIngredientReason = ingredient => {
  const hasTitle = Boolean(resolveIngredientDisplayTitle(ingredient))
  const hasProductId = Boolean(
    ingredient?.productId ||
      ingredient?._id ||
      ingredient?.venueProductId ||
      ingredient?.product?.venueProductId,
  )
  const hasSomeData = Boolean(
    ingredient?.sku ||
      ingredient?.title ||
      ingredient?.product ||
      ingredient?.productId,
  )
  const hasValidProductData =
    hasProductId &&
    (hasTitle || resolveIngredientDisplaySku(ingredient) || ingredient?.price)
  const isActuallyIncomplete = hasSomeData && !hasValidProductData
  const needsTitleLookup =
    hasProductId &&
    !hasTitle &&
    !resolveIngredientDisplaySku(ingredient) &&
    !ingredient?.price
  const isIncomplete = isActuallyIncomplete && !needsTitleLookup

  if (!isIncomplete) {
    return null
  }

  return !hasProductId ? 'Product not found' : 'Missing product title'
}

export const detectMissingRecipeProducts = (
  ingredients = [],
  { shoppingListProductIds } = {},
) => {
  const missing = []

  ;(ingredients || []).forEach((ingredient, index) => {
    const incompleteReason = getIncompleteIngredientReason(ingredient)
    if (incompleteReason) {
      missing.push({ index, ingredient, reason: incompleteReason })
      return
    }

    if (
      shoppingListProductIds?.size &&
      !isIngredientOnShoppingList(ingredient, shoppingListProductIds)
    ) {
      const venueProductId = resolveIngredientVenueProductId(ingredient)
      const catalogueProductId = resolveIngredientCatalogueProductId(ingredient)

      if (venueProductId || catalogueProductId) {
        missing.push({
          index,
          ingredient,
          reason: 'Not on shopping list',
        })
      }
    }
  })

  return missing
}

export const getMissingIngredientLabel = missing => {
  const title = resolveIngredientDisplayTitle(missing?.ingredient)
  if (title) {
    return title
  }

  const sku = resolveIngredientDisplaySku(missing?.ingredient)
  if (sku) {
    return `Product (SKU: ${sku})`
  }

  return 'Unknown ingredient'
}

export const formatMissingProductsBody = (count, template) =>
  String(template || '').replace('{count}', String(count))

export const hasRecipeMissingProducts = (recipe, shoppingListProductIds) =>
  detectMissingRecipeProducts(recipe?.ingredients || [], {
    shoppingListProductIds,
  }).length > 0
